import { useAuth0 } from '@auth0/auth0-vue';
import { defineStore } from 'pinia';
import isEmpty from 'lodash/isEmpty';
import { AuthUser } from '../entities/authUser.entity';
import { postSession } from '@/utils/session-channel';
import { classifyRefreshError, getAuthErrorCode } from '@/utils/auth-error.utils';
import { authUserStorage, dashboardPeriodStorage, lastActivityStorage } from '@/storages/browser.storage';
import { useModuleStore } from '@/stores/module.store';

const MAX_REFRESH_ATTEMPTS = 3;
// Backoff before the next retry, indexed by the attempt that just failed
// (index 0 is unused; attempt 1 → 500ms, attempt 2 → 1500ms).
const REFRESH_BACKOFF_MS = [0, 500, 1500];

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const useAuthStore = defineStore('auth-store', () => {
  let authUser: AuthUser;
  let isAuthenticated: boolean = false;

  const { getAccessTokenSilently, logout } = useAuth0();

  // This function is used to clear all browser storages which is required to be cleared on logout
  const clearBrowserStorages = (): void => {
    dashboardPeriodStorage.remove();
    authUserStorage.remove();
    lastActivityStorage.remove(); // drop the idle clock so the next session starts fresh
  };

  // `broadcast` defaults to true so a locally-initiated signout (definitive auth
  // error, idle logout, user action) signs out every tab. A tab that signs out
  // because it RECEIVED a signout message passes false to avoid a re-broadcast.
  const signout = (broadcast = true): void => {
    authUser = undefined;
    isAuthenticated = false;
    clearBrowserStorages();
    useModuleStore().resetModuleStore();
    if (broadcast) {
      postSession({ type: 'signout' });
    }
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Re-hydrate the in-memory user/token from shared localStorage. Used by the
  // cross-tab `token-updated` handler so a refresh in one tab keeps the others
  // consistent without each tab performing its own refresh.
  const reloadFromStorage = (): void => {
    authUser = authUserStorage.get();
    isAuthenticated = !isEmpty(authUser);
  };

  const setUser = (user: AuthUser): void => {
    // set new state to store
    authUser = {
      ...authUser,
      ...user,
    };

    if (!isEmpty(authUser)) {
      isAuthenticated = true;
      authUserStorage.set(authUser);
    }
  };

  const setAccessToken = (token: string): void => {
    // Store access token to local storage
    authUser = {
      ...authUser,
      accessToken: token,
    };
    authUserStorage.set(authUser);
  };

  const getAccessToken = (): string => {
    authUser = authUserStorage.get();

    return authUser?.accessToken;
  };

  const fetchAndStoreAccessToken = async (): Promise<string> => {
    // Get the access token
    const token = await getAccessTokenSilently();

    // Store the access token
    setAccessToken(token);

    // Tell other tabs a fresh token is in shared storage so they re-sync.
    postSession({ type: 'token-updated' });

    return token;
  };

  // Reactive refresh: classify the error, retry transient failures with backoff,
  // log every step (never the token/PII), and sign out ONLY on a definitive auth
  // error. Transient-but-exhausted surfaces the error and keeps the session — the
  // next API 401 will drive another refresh attempt.
  const refreshAccessToken = async (): Promise<string | undefined> => {
    for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
      try {
        const token = await fetchAndStoreAccessToken();
        if (attempt > 1) {
          console.info(`[auth:refresh] recovered on attempt ${attempt}/${MAX_REFRESH_ATTEMPTS}`);
        }
        return token;
      } catch (error) {
        const kind = classifyRefreshError(error);
        console.warn(`[auth:refresh] attempt ${attempt}/${MAX_REFRESH_ATTEMPTS} failed`, {
          kind,
          code: getAuthErrorCode(error),
          name: (error as { name?: string } | null)?.name,
        });

        if (kind === 'definitive') {
          console.error('[auth:refresh] definitive auth error → signout', { code: getAuthErrorCode(error) });
          signout();
          return undefined; // signout is the ONLY logout path
        }

        if (attempt < MAX_REFRESH_ATTEMPTS) {
          await delay(REFRESH_BACKOFF_MS[attempt]);
          continue;
        }

        // Transient error, retries exhausted: keep the session and surface the
        // error so the caller can react; a later 401 recovers via another refresh.
        console.error('[auth:refresh] transient error, retries exhausted → session kept');
        throw error;
      }
    }
    // Unreachable: every iteration returns or throws. Required for TS control-flow
    // analysis since it can't prove MAX_REFRESH_ATTEMPTS >= 1.
    return undefined;
  };

  const getUser = (): AuthUser => {
    if (isEmpty(authUser)) {
      authUser = authUserStorage.get();
      isAuthenticated = !isEmpty(authUser);
    }

    return authUser;
  };

  const hasAuthenticated = (): boolean => {
    return isAuthenticated;
  };

  return {
    fetchAndStoreAccessToken,
    hasAuthenticated,
    getAccessToken,
    setAccessToken,
    refreshAccessToken,
    reloadFromStorage,
    setUser,
    getUser,
    signout,
  };
});
